import { useCallback, useMemo } from "react"
import { graphql, useLazyLoadQuery } from "react-relay"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import {
  usePhoneNumberCountryListQuery,
  usePhoneNumberCountryListQuery$data,
} from "@/modules/patients/components/phoneNumber/hooks/__generated__/usePhoneNumberCountryListQuery.graphql"

type Response = usePhoneNumberCountryListQuery$data["displayableCountries"]
const usePhoneNumberCountryList = (keyword: string) => {
  const language = useCurrentLanguage()

  const { displayableCountries: countries } = useLazyLoadQuery<usePhoneNumberCountryListQuery>(
    graphql`
      query usePhoneNumberCountryListQuery($language: String!) {
        displayableCountries(language: $language) {
          id
          language
          countryIsoCode2
          countryName
          countryLookup {
            countryIsoCode2
            mask
            dialCode
          }
        }
      }
    `,
    { language },
  )

  const filteredCountries = useCallback(
    (cities: Response) => {
      return cities.filter((item) => language.toUpperCase() === item.language) || []
    },
    [language],
  )
  const countryOptions = useMemo(() => {
    const activeCountry = filteredCountries(countries)
    return activeCountry.map((item) => ({
      id: item.id,
      label: item.countryName,
      value: item.countryName,
      code: item.countryLookup?.countryIsoCode2 || "",
      old: item.countryLookup?.mask,
      mask: item.countryLookup?.mask || "",
      dialCode: item.countryLookup?.dialCode || "",
    }))
  }, [countries, filteredCountries])

  const filteredCountryOptions = useMemo(() => {
    return countryOptions.filter((country) => country.label.toLowerCase().includes(keyword.toLowerCase()))
  }, [countryOptions, keyword])

  const mostRecent = useMemo(() => {
    return countryOptions.filter(
      (country) => country.code === "DE" || country.code === "CH" || country.code === "IT" || country.code === "FR",
    )
  }, [countryOptions])

  const getCountryByPhoneNumber = useCallback(
    (phoneNumber: string) =>
      countryOptions.find((country) => country.dialCode.length !== 0 && phoneNumber.includes(country.dialCode)),
    [countryOptions],
  )

  return {
    filteredCountryOptions,
    mostRecent,
    countryOptions,
    getCountryByPhoneNumber,
  }
}

export { usePhoneNumberCountryList }
